import { z } from "zod";
import { organizationEntityReferenceSchema } from "./reference.js";
import {
  AUTOMATION_CATCH_UP_POLICIES,
  AUTOMATION_CONCURRENCY_POLICIES,
  AUTOMATION_OUTPUT_MODES,
  AUTOMATION_STATUSES,
  CHAT_CONTEXT_ENTITY_TYPES,
  CHAT_CONVERSATION_STATUSES,
  CHAT_ISSUE_CREATION_MODES,
  CHAT_MESSAGE_KINDS,
  CHAT_MESSAGE_ROLES,
  CHAT_MESSAGE_STATUSES,
  ISSUE_STATUSES,
} from "../constants.js";
import type { ChatInlineAnnotation, ChatInlineAnnotationInput } from "../types/chat.js";

export const chatConversationStatusSchema = z.enum(CHAT_CONVERSATION_STATUSES);
export const chatIssueCreationModeSchema = z.enum(CHAT_ISSUE_CREATION_MODES);
export const chatMessageRoleSchema = z.enum(CHAT_MESSAGE_ROLES);
export const chatMessageKindSchema = z.enum(CHAT_MESSAGE_KINDS);
export const chatMessageStatusSchema = z.enum(CHAT_MESSAGE_STATUSES);
export const chatContextEntityTypeSchema = z.enum(CHAT_CONTEXT_ENTITY_TYPES);
export const chatGenerationStatusSchema = z.enum([
  "starting",
  "active",
  "running",
  "waiting_for_network",
  "tool_busy",
  "closing",
  "stop_requested",
  "stopping",
  "completed",
  "failed",
  "stopped",
  "aborted",
  "interrupted_unverified",
  "control_lost",
]);
export const chatGenerationControlStateSchema = z.enum([
  "unregistered",
  "ready",
  "stopping",
  "terminal",
  "control_lost",
]);
export const chatQueueDeliveryIntentSchema = z.enum(["queue", "steer"]);
export const chatControlDispositionSchema = z.enum([
  "pending",
  "accepted_current",
  "acceptance_unknown",
  "reconciled_current",
  "continuation_pending",
  "waiting_safe_boundary",
  "running_next",
  "delivered",
  "stop_requested",
  "stopping",
  "stopped",
  "interrupted_unverified",
  "cancellation_unverified",
  "control_lost",
  "failed_actionable",
  "cancelled",
]);
export const chatProviderControlDispositionSchema = z.enum([
  "not_sent",
  "sent",
  "acknowledged",
  "rejected",
  "timed_out",
  "connection_lost",
  "waiting_safe_boundary",
  "unverified",
]);
export const chatControlActionKindSchema = z.enum(["stop", "steer"]);
export const chatGenerationEventKindSchema = z.enum([
  "generation_started",
  "runtime_output",
  "assistant_delta",
  "transcript",
  "client_checkpoint",
  "steer_requested",
  "steer_provider_sent",
  "steer_acknowledged",
  "continuation_scheduled",
  "stop_requested",
  "output_cutoff",
  "runtime_terminal",
  "late_output_dropped",
  "terminal_projection_requested",
  "terminal_projected",
]);
export const chatTerminalOutboxStatusSchema = z.enum([
  "pending",
  "claimed",
  "retry_wait",
  "projected",
  "failed_actionable",
]);
export const chatQueuedMessageStatusSchema = z.enum([
  "queued",
  "steer_pending",
  "accepted_current",
  "acceptance_unknown",
  "reconciled_current",
  "continuation_pending",
  "running_next",
  "delivered",
  "failed_actionable",
  "steered",
  "dequeue_claimed",
  "running",
  "completed",
  "cancelled",
  "failed",
]);

const chatControlActionIdSchema = z.string().uuid();
const chatBodyHashSchema = z.string().regex(/^[a-f0-9]{64}$/i, "Expected a SHA-256 body hash");

export const MAX_CHAT_INLINE_ANNOTATIONS = 10;
export const MAX_CHAT_INLINE_ANNOTATION_SELECTED_TEXT_LENGTH = 4_000;
export const MAX_CHAT_INLINE_ANNOTATION_COMMENT_LENGTH = 2_000;
export const MAX_CHAT_INLINE_ANNOTATION_TOTAL_TEXT_LENGTH = 16_000;
export const MAX_CHAT_INLINE_ANNOTATION_CONTEXT_LENGTH = 160;
export const MAX_CHAT_INLINE_ANNOTATION_ATTACHMENTS = 10;
export const MAX_CHAT_INLINE_ANNOTATION_RUN_MEMBER_IDS = 100;
export const MAX_CHAT_INLINE_ANNOTATION_RUN_ENTRY_ID_LENGTH = 200;

const chatInlineAnnotationCommentSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  },
  z.string().max(MAX_CHAT_INLINE_ANNOTATION_COMMENT_LENGTH).nullable().optional(),
);
const chatInlineAnnotationSelectedTextSchema = z.string()
  .min(1)
  .max(MAX_CHAT_INLINE_ANNOTATION_SELECTED_TEXT_LENGTH)
  .refine((value) => value.trim().length > 0, "Selected text must not be blank");
const chatInlineAnnotationSourceHashSchema = z.string()
  .regex(/^[a-f0-9]{64}$/, "Expected a lowercase SHA-256 source hash");
const chatInlineAnnotationRunEntryIdSchema = z.union([
  z.number().int().positive(),
  z.string().trim().min(1).max(MAX_CHAT_INLINE_ANNOTATION_RUN_ENTRY_ID_LENGTH),
]);

const chatInlineAnnotationShape = {
  id: z.string().uuid(),
  selectedText: chatInlineAnnotationSelectedTextSchema,
  comment: chatInlineAnnotationCommentSchema,
  sourceConversationId: z.string().uuid(),
  sourceHash: chatInlineAnnotationSourceHashSchema,
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
  prefix: z.string().max(MAX_CHAT_INLINE_ANNOTATION_CONTEXT_LENGTH),
  suffix: z.string().max(MAX_CHAT_INLINE_ANNOTATION_CONTEXT_LENGTH),
  attachmentIds: z.array(z.string().uuid()).default([]),
};

function refineChatInlineAnnotation(
  annotation: {
    surface: "assistant_body" | "process_transcript" | "agent_run_transcript" | "workspace_file" | "local_file";
    transcriptKind?: "thinking" | "assistant";
    generationId?: string;
    generationSeqStart?: number;
    generationSeqEnd?: number;
    sourceEntryId?: string | number;
    sourceMemberIds?: Array<string | number>;
    start?: number;
    end?: number;
    attachmentIds: string[];
    attachmentFileIndexes?: number[];
  },
  ctx: z.RefinementCtx,
) {
  if (
    annotation.start !== undefined
    && annotation.end !== undefined
    && annotation.end <= annotation.start
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Annotation end must be greater than start",
      path: ["end"],
    });
  }

  const attachmentIds = new Set<string>();
  annotation.attachmentIds.forEach((attachmentId, index) => {
    if (attachmentIds.has(attachmentId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Annotation attachment ids must be unique",
        path: ["attachmentIds", index],
      });
    }
    attachmentIds.add(attachmentId);
  });
  const attachmentFileIndexes = new Set<number>();
  annotation.attachmentFileIndexes?.forEach((fileIndex, index) => {
    if (attachmentFileIndexes.has(fileIndex)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Annotation attachment file indexes must be unique",
        path: ["attachmentFileIndexes", index],
      });
    }
    attachmentFileIndexes.add(fileIndex);
  });

  if (annotation.surface === "agent_run_transcript") {
    const memberIds = annotation.sourceMemberIds ?? [];
    const sourceMemberIds = new Set<string>();
    memberIds.forEach((memberId, index) => {
      const key = String(memberId);
      if (sourceMemberIds.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Agent run annotation source member ids must be unique",
          path: ["sourceMemberIds", index],
        });
      }
      sourceMemberIds.add(key);
    });
  }

  if (
    annotation.surface === "process_transcript"
    && annotation.generationSeqStart !== undefined
    && annotation.generationSeqEnd !== undefined
    && annotation.generationSeqEnd < annotation.generationSeqStart
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Generation sequence end must be greater than or equal to start",
      path: ["generationSeqEnd"],
    });
  }
}

const chatAssistantInlineAnnotationSchema = z.object({
  ...chatInlineAnnotationShape,
  surface: z.literal("assistant_body"),
  sourceMessageId: z.string().uuid(),
}).strict();
const chatProcessInlineAnnotationSchema = z.object({
  ...chatInlineAnnotationShape,
  surface: z.literal("process_transcript"),
  sourceMessageId: z.string().uuid(),
  transcriptKind: z.enum(["thinking", "assistant"]),
  generationId: z.string().uuid(),
  generationSeqStart: z.number().int().nonnegative(),
  generationSeqEnd: z.number().int().nonnegative(),
}).strict();
const chatAgentRunInlineAnnotationSchema = z.object({
  id: z.string().uuid(),
  selectedText: chatInlineAnnotationSelectedTextSchema,
  comment: chatInlineAnnotationCommentSchema,
  sourceHash: chatInlineAnnotationSourceHashSchema,
  surface: z.literal("agent_run_transcript"),
  sourceRunId: z.string().uuid(),
  sourceAgentId: z.string().uuid(),
  anchorKind: z.enum(["text", "transition"]),
  sourceEntryId: chatInlineAnnotationRunEntryIdSchema,
  sourceMemberIds: z.array(chatInlineAnnotationRunEntryIdSchema)
    .min(1)
    .max(MAX_CHAT_INLINE_ANNOTATION_RUN_MEMBER_IDS),
  attachmentIds: z.array(z.string().uuid()).default([]),
}).strict();
const chatWorkspaceFileInlineAnnotationSchema = z.object({
  ...chatInlineAnnotationShape,
  surface: z.literal("workspace_file"),
  sourceFilePath: z.string().min(1).max(4_096),
  sourceLibraryEntryId: z.string().uuid().nullable(),
  sourceRenderMode: z.enum(["markdown", "text"]),
}).strict();
const chatLocalFileInlineAnnotationSchema = z.object({
  ...chatInlineAnnotationShape,
  surface: z.literal("local_file"),
  sourceFilePath: z.string().min(1).max(8_192),
  sourceRenderMode: z.enum(["markdown", "text"]),
}).strict();

export const chatInlineAnnotationSchema = z.discriminatedUnion("surface", [
  chatAssistantInlineAnnotationSchema,
  chatProcessInlineAnnotationSchema,
  chatAgentRunInlineAnnotationSchema,
  chatWorkspaceFileInlineAnnotationSchema,
  chatLocalFileInlineAnnotationSchema,
])
  .superRefine(refineChatInlineAnnotation);

const chatAssistantInlineAnnotationInputSchema = chatAssistantInlineAnnotationSchema.extend({
  attachmentFileIndexes: z.array(z.number().int().nonnegative()).optional(),
}).strict();
const chatProcessInlineAnnotationInputSchema = chatProcessInlineAnnotationSchema.extend({
  attachmentFileIndexes: z.array(z.number().int().nonnegative()).optional(),
}).strict();
const chatAgentRunInlineAnnotationInputSchema = chatAgentRunInlineAnnotationSchema.extend({
  attachmentFileIndexes: z.array(z.number().int().nonnegative()).optional(),
}).strict();
const chatWorkspaceFileInlineAnnotationInputSchema = chatWorkspaceFileInlineAnnotationSchema.extend({
  attachmentFileIndexes: z.array(z.number().int().nonnegative()).optional(),
}).strict();
const chatLocalFileInlineAnnotationInputSchema = chatLocalFileInlineAnnotationSchema.extend({
  attachmentFileIndexes: z.array(z.number().int().nonnegative()).optional(),
}).strict();

export const chatInlineAnnotationInputSchema = z.discriminatedUnion("surface", [
  chatAssistantInlineAnnotationInputSchema,
  chatProcessInlineAnnotationInputSchema,
  chatAgentRunInlineAnnotationInputSchema,
  chatWorkspaceFileInlineAnnotationInputSchema,
  chatLocalFileInlineAnnotationInputSchema,
])
  .superRefine(refineChatInlineAnnotation);

function createChatInlineAnnotationsSchema<T extends z.ZodTypeAny>(annotationSchema: T) {
  return z.array(annotationSchema)
    .max(MAX_CHAT_INLINE_ANNOTATIONS)
    .superRefine((annotations, ctx) => {
      let totalTextLength = 0;
      let attachmentReferenceCount = 0;
      const annotationIds = new Set<string>();
      const sourceRanges = new Set<string>();
      const attachmentIds = new Set<string>();
      const attachmentFileIndexes = new Set<number>();

      annotations.forEach((annotation, annotationIndex) => {
        totalTextLength += annotation.selectedText.length + (annotation.comment?.length ?? 0);
        attachmentReferenceCount += annotation.attachmentIds.length
          + (annotation.attachmentFileIndexes?.length ?? 0);
        if (annotationIds.has(annotation.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Annotation ids must be unique across the message",
            path: [annotationIndex, "id"],
          });
        }
        annotationIds.add(annotation.id);
        const sourceRangeKey = annotation.surface === "agent_run_transcript"
          ? JSON.stringify([
            annotation.surface,
            annotation.sourceRunId,
            annotation.sourceAgentId,
            annotation.anchorKind,
            annotation.sourceEntryId,
            annotation.sourceMemberIds,
          ])
          : JSON.stringify([
            annotation.sourceConversationId,
            annotation.sourceMessageId,
            annotation.sourceFilePath,
            annotation.surface,
            annotation.surface === "process_transcript" ? annotation.transcriptKind : null,
            annotation.surface === "process_transcript" ? annotation.generationId : null,
            annotation.surface === "process_transcript" ? annotation.generationSeqStart : null,
            annotation.surface === "process_transcript" ? annotation.generationSeqEnd : null,
            annotation.start,
            annotation.end,
          ]);
        if (sourceRanges.has(sourceRangeKey)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Annotation source ranges must be unique across the message",
            path: [annotationIndex, annotation.surface === "agent_run_transcript" ? "sourceEntryId" : "start"],
          });
        }
        sourceRanges.add(sourceRangeKey);
        annotation.attachmentIds.forEach((attachmentId: string, attachmentIndex: number) => {
          if (attachmentIds.has(attachmentId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Annotation attachment ids must be unique across the message",
              path: [annotationIndex, "attachmentIds", attachmentIndex],
            });
          }
          attachmentIds.add(attachmentId);
        });
        annotation.attachmentFileIndexes?.forEach((fileIndex: number, attachmentIndex: number) => {
          if (attachmentFileIndexes.has(fileIndex)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Annotation attachment file indexes must be unique across the message",
              path: [annotationIndex, "attachmentFileIndexes", attachmentIndex],
            });
          }
          attachmentFileIndexes.add(fileIndex);
        });
      });

      if (totalTextLength > MAX_CHAT_INLINE_ANNOTATION_TOTAL_TEXT_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Annotation selected text and comments must not exceed ${MAX_CHAT_INLINE_ANNOTATION_TOTAL_TEXT_LENGTH} characters`,
        });
      }
      if (attachmentReferenceCount > MAX_CHAT_INLINE_ANNOTATION_ATTACHMENTS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Annotations must not reference more than ${MAX_CHAT_INLINE_ANNOTATION_ATTACHMENTS} attachments`,
        });
      }
    });
}

export const chatInlineAnnotationsSchema = createChatInlineAnnotationsSchema(chatInlineAnnotationSchema);
export const chatInlineAnnotationsInputSchema = createChatInlineAnnotationsSchema(chatInlineAnnotationInputSchema);

export function normalizeChatInlineAnnotations(
  annotations: readonly ChatInlineAnnotationInput[],
): ChatInlineAnnotation[] {
  const parsed = chatInlineAnnotationsInputSchema.parse(annotations);
  const canonical = parsed.map(({ attachmentFileIndexes: _attachmentFileIndexes, ...annotation }) => annotation);
  return chatInlineAnnotationsSchema.parse(canonical);
}

export const createChatContextLinkSchema = z.object({
  entityType: chatContextEntityTypeSchema,
  entityId: z.string().min(1),
  metadata: z.record(z.unknown()).optional().nullable(),
});

export const chatModelOverrideSchema = z.string().trim().min(1).max(120).nullable();
export const chatEffortOverrideSchema = z.string().trim().min(1).max(120).nullable();

export const chatDraftSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  summary: z.string().trim().max(5000).optional().nullable(),
  preferredAgentId: z.string().uuid().optional().nullable(),
  modelOverride: chatModelOverrideSchema.optional(),
  effortOverride: chatEffortOverrideSchema.optional(),
  issueCreationMode: chatIssueCreationModeSchema.optional(),
  planMode: z.boolean().optional(),
  contextLinks: z.array(createChatContextLinkSchema).optional().default([]),
});

export const createChatConversationSchema = chatDraftSchema.extend({
  initialMessage: z.object({
    body: z.string().trim().min(1).max(20000),
  }),
});

export const createChatFirstTurnSchema = chatDraftSchema.extend({
  groupId: z.string().uuid().optional(),
  body: z.string().trim().max(20000).default(""),
  clientMutationId: z.string().trim().min(1).max(120).optional(),
  inlineAnnotations: chatInlineAnnotationsInputSchema.optional().default([]),
}).superRefine((value, ctx) => {
  if (value.body.length === 0 && value.inlineAnnotations.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Chat message body or at least one inline annotation is required",
      path: ["body"],
    });
  }
});

export const setChatProjectContextSchema = z.object({
  projectId: z.string().uuid().optional().nullable(),
});

export const updateChatConversationSchema = chatDraftSchema
  .partial()
  .extend({
    status: chatConversationStatusSchema.optional(),
    routedAgentId: z.string().uuid().optional().nullable(),
    primaryIssueId: z.string().uuid().optional().nullable(),
    resolvedAt: z.string().datetime().optional().nullable(),
  });

export const forkChatConversationSchema = z.object({
  sourceMessageId: z.string().uuid().optional().nullable(),
  title: z.string().trim().min(1).max(200).optional(),
});

export const createSideChatSchema = z.object({
  sourceMessageId: z.string().uuid(),
  clientMutationId: z.string().trim().min(1).max(120),
  preferredAgentId: z.string().uuid().optional(),
}).strict();

export const addChatMessageSchema = z.object({
  body: z.string().trim().max(20000).default(""),
  clientMutationId: z.string().trim().min(1).max(120).optional(),
  inlineAnnotations: chatInlineAnnotationsInputSchema.optional(),
  editUserMessageId: z.string().uuid().optional().nullable(),
  queuedMessageId: z.string().uuid().optional().nullable(),
  modelOverride: chatModelOverrideSchema.optional().nullable(),
  effortOverride: chatEffortOverrideSchema.optional().nullable(),
}).superRefine((value, ctx) => {
  if (value.body.length === 0 && (value.inlineAnnotations?.length ?? 0) === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Chat message body or at least one inline annotation is required",
      path: ["body"],
    });
  }
});

export const chatQueuedMessagePayloadSchema = z.object({
  body: z.string().trim().max(20000).default(""),
  attachmentIds: z.array(z.string().uuid()).optional().default([]),
  inlineAnnotations: chatInlineAnnotationsInputSchema.optional().default([]),
  projectId: z.string().uuid().optional().nullable(),
  skillRefs: z.array(z.string().trim().min(1).max(240)).optional().default([]),
  accessMode: z.string().trim().min(1).max(120).optional().nullable(),
  agentId: z.string().uuid().optional().nullable(),
  model: z.string().trim().min(1).max(120).optional().nullable(),
  effort: z.string().trim().min(1).max(120).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
}).superRefine((value, ctx) => {
  if (value.body.length === 0 && value.inlineAnnotations.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Queued message body or at least one inline annotation is required",
      path: ["body"],
    });
  }
});

export const createChatQueuedMessageSchema = z.object({
  clientMutationId: z.string().trim().min(1).max(120),
  expectedGenerationId: z.string().uuid().optional().nullable(),
  payload: chatQueuedMessagePayloadSchema,
});

const chatQueuedMessageUpdatePayloadSchema = z.object({
  body: z.string().trim().max(20000).default(""),
  attachmentIds: z.array(z.string().uuid()).optional().default([]),
  inlineAnnotations: chatInlineAnnotationsInputSchema.optional(),
  projectId: z.string().uuid().optional().nullable(),
  skillRefs: z.array(z.string().trim().min(1).max(240)).optional().default([]),
  accessMode: z.string().trim().min(1).max(120).optional().nullable(),
  agentId: z.string().uuid().optional().nullable(),
  model: z.string().trim().min(1).max(120).optional().nullable(),
  effort: z.string().trim().min(1).max(120).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
}).superRefine((value, ctx) => {
  if (
    value.body.length === 0
    && value.inlineAnnotations !== undefined
    && value.inlineAnnotations.length === 0
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Queued message body or at least one inline annotation is required",
      path: ["body"],
    });
  }
});

export const updateChatQueuedMessageSchema = z.object({
  version: z.number().int().positive(),
  payload: chatQueuedMessageUpdatePayloadSchema,
});

export const cancelChatQueuedMessageSchema = z.object({
  version: z.number().int().positive().optional(),
});

export const steerChatQueuedMessageSchema = z.object({
  expectedActiveGenerationId: z.string().uuid().optional().nullable(),
  controlActionId: chatControlActionIdSchema.optional(),
  expectedAttemptEpoch: z.number().int().nonnegative().optional(),
  expectedControlVersion: z.number().int().nonnegative().optional(),
  lastCommittedRenderSeq: z.number().int().nonnegative().optional(),
  renderedBodyHash: chatBodyHashSchema.optional(),
}).superRefine((value, ctx) => {
  const hasGenerationTarget = Boolean(value.expectedActiveGenerationId);
  const hasFenceVersion = value.expectedAttemptEpoch !== undefined
    || value.expectedControlVersion !== undefined;
  if ((value.lastCommittedRenderSeq === undefined) !== (value.renderedBodyHash === undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["renderedBodyHash"], message: "Steer render sequence and body hash must be supplied together" });
  }
  if (!hasFenceVersion && (!value.controlActionId || !hasGenerationTarget)) return;
  if (!value.controlActionId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["controlActionId"], message: "Required for durable Steer control" });
  }
  if (!value.expectedActiveGenerationId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedActiveGenerationId"], message: "Required for durable Steer control" });
  }
  if (value.expectedAttemptEpoch === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedAttemptEpoch"], message: "Required for durable Steer control" });
  }
  if (value.expectedControlVersion === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedControlVersion"], message: "Required for durable Steer control" });
  }
});

export const stopChatGenerationSchema = z.object({
  controlActionId: chatControlActionIdSchema.optional(),
  expectedGenerationId: z.string().uuid().optional(),
  expectedAttemptEpoch: z.number().int().nonnegative().optional(),
  expectedControlVersion: z.number().int().nonnegative().optional(),
  lastCommittedRenderSeq: z.number().int().nonnegative().optional(),
  renderedBodyHash: chatBodyHashSchema.optional(),
}).superRefine((value, ctx) => {
  const generationFenceStarted = value.expectedGenerationId !== undefined
    || value.expectedAttemptEpoch !== undefined
    || value.expectedControlVersion !== undefined;
  if (generationFenceStarted) {
    if (!value.expectedGenerationId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedGenerationId"], message: "Required for fenced Stop control" });
    }
    if (value.expectedAttemptEpoch === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedAttemptEpoch"], message: "Required for fenced Stop control" });
    }
    if (value.expectedControlVersion === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expectedControlVersion"], message: "Required for fenced Stop control" });
    }
  }
  if ((value.lastCommittedRenderSeq === undefined) !== (value.renderedBodyHash === undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["renderedBodyHash"], message: "Stop render sequence and body hash must be supplied together" });
  }
});

export const chatClientCheckpointSchema = z.object({
  generationId: z.string().uuid(),
  attemptEpoch: z.number().int().positive(),
  generationSeq: z.number().int().nonnegative(),
  renderedBodyHash: chatBodyHashSchema,
});

export const appendChatGenerationEventSchema = z.object({
  generationId: z.string().uuid(),
  generationSeq: z.number().int().nonnegative(),
  attemptEpoch: z.number().int().positive(),
  eventKind: chatGenerationEventKindSchema,
  payload: z.record(z.unknown()).default({}),
  bodyOffset: z.number().int().nonnegative().optional().nullable(),
  bodyLength: z.number().int().nonnegative().optional().nullable(),
  assistantMessageId: z.string().uuid().optional().nullable(),
  runId: z.string().uuid().optional().nullable(),
  controlActionId: chatControlActionIdSchema.optional().nullable(),
  queueItemId: z.string().uuid().optional().nullable(),
});

const chatRichReferenceDisplaySchema = z.enum(["card", "inline"]);
const chatIssueIdentifierSchema = z.string().trim().min(1).max(64).regex(/^[A-Z0-9][A-Z0-9-]*$/i);
const chatAskUserIdentifierSchema = z.string().trim().min(1).max(64).regex(/^[a-zA-Z0-9_-]+$/);

export const chatAskUserOptionSchema = z.object({
  id: chatAskUserIdentifierSchema,
  label: z.string().trim().min(1).max(80),
  description: z.string().trim().min(1).max(220).optional(),
  recommended: z.boolean().optional(),
});

export const chatAskUserQuestionSchema = z.object({
  id: chatAskUserIdentifierSchema,
  header: z.string().trim().min(1).max(32).optional(),
  question: z.string().trim().min(1).max(240),
  options: z.array(chatAskUserOptionSchema).min(2).max(3),
  selectionMode: z.enum(["single", "multiple"]).optional(),
  allowFreeform: z.boolean().optional(),
}).superRefine((question, ctx) => {
  const optionIds = new Set<string>();
  question.options.forEach((option, index) => {
    if (optionIds.has(option.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Option ids must be unique within each question",
        path: ["options", index, "id"],
      });
      return;
    }
    optionIds.add(option.id);
  });
});

export const chatAskUserRequestSchema = z.object({
  questions: z.array(chatAskUserQuestionSchema).min(1).max(3),
}).superRefine((request, ctx) => {
  const questionIds = new Set<string>();
  request.questions.forEach((question, index) => {
    if (questionIds.has(question.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Question ids must be unique within requestUserInput",
        path: ["questions", index, "id"],
      });
      return;
    }
    questionIds.add(question.id);
  });
});

const chatIssueRichReferenceSchema = z.object({
  type: z.literal("issue"),
  issueId: z.string().uuid().optional(),
  identifier: chatIssueIdentifierSchema.optional(),
  display: chatRichReferenceDisplaySchema.optional(),
}).refine((value) => Boolean(value.issueId || value.identifier), {
  message: "issueId or identifier is required",
});

const chatIssueCommentRichReferenceSchema = z.object({
  type: z.literal("issue_comment"),
  issueId: z.string().uuid().optional(),
  identifier: chatIssueIdentifierSchema.optional(),
  commentId: z.string().uuid(),
  display: chatRichReferenceDisplaySchema.optional(),
}).refine((value) => Boolean(value.issueId || value.identifier), {
  message: "issueId or identifier is required",
});

export const chatRichReferenceSchema = z.union([
  chatIssueRichReferenceSchema,
  chatIssueCommentRichReferenceSchema,
]);

export const chatRichReferencesSchema = z.array(chatRichReferenceSchema).max(5);

export function chatRichReferencesFromStructuredPayload(payload: unknown) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
  const rawReferences = (payload as Record<string, unknown>).richReferences;
  if (!Array.isArray(rawReferences)) return [];

  return rawReferences
    .map((reference) => chatRichReferenceSchema.safeParse(reference))
    .filter((result): result is z.SafeParseSuccess<z.infer<typeof chatRichReferenceSchema>> => result.success)
    .map((result) => result.data)
    .slice(0, 5);
}

export function chatInlineAnnotationsFromStructuredPayload(payload: unknown): ChatInlineAnnotation[] {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
  const rawAnnotations = (payload as Record<string, unknown>).inlineAnnotations;
  const parsed = chatInlineAnnotationsSchema.safeParse(rawAnnotations);
  return parsed.success && parsed.data.length > 0 ? parsed.data : [];
}

export function chatAskUserRequestFromStructuredPayload(payload: unknown) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const rawRequest = (payload as Record<string, unknown>).requestUserInput;
  const parsed = chatAskUserRequestSchema.safeParse(rawRequest);
  return parsed.success ? parsed.data : null;
}

export const chatAutomationCreateSchema = z.object({
  title: z.string().trim().min(1).max(200),
  instructions: z.string().trim().max(20_000).optional().nullable(),
  description: z.string().trim().max(20_000).optional().nullable(),
  projectId: z.string().uuid().optional().nullable(),
  goalId: z.string().uuid().optional().nullable(),
  parentIssueId: z.string().uuid().optional().nullable(),
  priority: z.enum(["critical", "high", "medium", "low"]).optional().default("medium"),
  status: z.enum(AUTOMATION_STATUSES).optional().default("active"),
  concurrencyPolicy: z.enum(AUTOMATION_CONCURRENCY_POLICIES).optional().default("coalesce_if_active"),
  catchUpPolicy: z.enum(AUTOMATION_CATCH_UP_POLICIES).optional().default("skip_missed"),
  outputMode: z.enum(AUTOMATION_OUTPUT_MODES).optional().default("track_issue"),
  schedule: z.object({
    cronExpression: z.string().trim().min(1),
    timezone: z.string().trim().min(1),
    label: z.string().trim().max(120).optional().nullable(),
    enabled: z.boolean().optional().default(true),
  }),
}).transform(({ description, instructions, ...value }) => ({
  ...value,
  instructions: instructions === undefined ? description : instructions,
}));

export function chatAutomationCreateFromStructuredPayload(payload: unknown) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const rawRequest = (payload as Record<string, unknown>).automationCreate;
  const parsed = chatAutomationCreateSchema.safeParse(rawRequest);
  return parsed.success ? parsed.data : null;
}

export function sanitizeChatStructuredPayload(payload: Record<string, unknown> | null | undefined) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const next = { ...payload };
  delete next.inlineVisuals;
  delete next.inlineVisualsV1;
  delete next.inlineAnnotations;
  const inlineAnnotations = chatInlineAnnotationsFromStructuredPayload(payload);
  if (inlineAnnotations.length > 0) {
    next.inlineAnnotations = inlineAnnotations;
  }
  const requestUserInput = chatAskUserRequestFromStructuredPayload(payload);
  if (requestUserInput) {
    next.requestUserInput = requestUserInput;
  } else {
    delete next.requestUserInput;
  }
  const richReferences = chatRichReferencesFromStructuredPayload(payload);
  if (richReferences.length > 0) {
    next.richReferences = richReferences;
  } else {
    delete next.richReferences;
  }
  const automationCreate = chatAutomationCreateFromStructuredPayload(payload);
  if (automationCreate) {
    next.automationCreate = automationCreate;
  } else {
    delete next.automationCreate;
  }
  return Object.keys(next).length > 0 ? next : null;
}

export const createChatAttachmentMetadataSchema = z.object({
  messageId: z.string().uuid(),
});

export const chatIssueProposalSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(20000),
  status: z.enum(ISSUE_STATUSES).optional().default("todo"),
  priority: z.enum(["critical", "high", "medium", "low"]).optional().default("medium"),
  projectId: organizationEntityReferenceSchema("project", "Project ID").optional().nullable(),
  goalId: organizationEntityReferenceSchema("goal", "Goal ID").optional().nullable(),
  parentId: organizationEntityReferenceSchema("issue", "Parent issue ID").optional().nullable(),
  assigneeAgentId: organizationEntityReferenceSchema("agent", "Assignee agent ID").optional().nullable(),
  assigneeUserId: z.string().trim().optional().nullable(),
  assigneeUnassignedReason: z.string().trim().min(1).max(500).optional().nullable(),
  reviewerAgentId: organizationEntityReferenceSchema("agent", "Reviewer agent ID").optional().nullable(),
  reviewerUserId: z.string().trim().optional().nullable(),
  labelIds: z.array(z.string().uuid()).optional(),
}).superRefine((proposal, ctx) => {
  const hasAssignee = Boolean(proposal.assigneeAgentId || proposal.assigneeUserId);
  const hasUnassignedReason = Boolean(proposal.assigneeUnassignedReason?.trim());
  if (hasAssignee && hasUnassignedReason) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Issue proposals with an assignee must not also declare an unassigned reason",
      path: ["assigneeUnassignedReason"],
    });
  }
  if (!hasAssignee && !hasUnassignedReason) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Issue proposals without an owner must include assigneeUnassignedReason",
      path: ["assigneeUnassignedReason"],
    });
  }
});

export const convertChatToIssueSchema = z.object({
  messageId: z.string().uuid().optional().nullable(),
  proposal: chatIssueProposalSchema.optional(),
});

export function chatIssueProposalFromStructuredPayload(payload: unknown) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const root = payload as Record<string, unknown>;
  const rawProposal =
    root.issueProposal && typeof root.issueProposal === "object" && !Array.isArray(root.issueProposal)
      ? root.issueProposal
      : root;
  const parsed = chatIssueProposalSchema.safeParse(rawProposal);
  return parsed.success ? parsed.data : null;
}

export const chatOperationProposalSchema = z.object({
  targetType: z.enum(["organization", "agent"]),
  targetId: z.string().min(1),
  summary: z.string().trim().min(1).max(500),
  patch: z.record(z.unknown()),
});

export const resolveChatOperationProposalSchema = z.object({
  action: z.enum(["approve", "reject", "requestRevision"]),
  decisionNote: z.string().trim().max(5000).optional().nullable(),
});

export const updateChatConversationUserStateSchema = z.object({
  pinned: z.boolean().optional(),
  unread: z.boolean().optional(),
});

export const updateMessengerThreadUserStateSchema = z.object({
  pinned: z.boolean().optional(),
});

export const createMessengerCustomGroupSchema = z.object({
  name: z.string().trim().min(1).max(80),
  icon: z.string().trim().min(1).max(24).optional().nullable(),
});

const messengerItemKeySchema = z.string().trim().min(1).max(240).refine((itemKey) => {
  if (!itemKey.startsWith("saved-view:")) return true;
  return z.string().uuid().safeParse(itemKey.slice("saved-view:".length)).success;
}, "Saved View item keys must contain a valid UUID");
const messengerItemKeysSchema = z.array(messengerItemKeySchema).min(1).max(50);

export const createMessengerCustomGroupWithEntriesSchema = createMessengerCustomGroupSchema.extend({
  itemKeys: messengerItemKeysSchema.optional(),
  threadKeys: messengerItemKeysSchema.optional(),
  anchorItemKey: messengerItemKeySchema.optional(),
  autoGenerateName: z.boolean().optional(),
}).superRefine((value, ctx) => {
  if (!value.itemKeys && !value.threadKeys) {
    ctx.addIssue({ code: "custom", message: "itemKeys or threadKeys is required", path: ["itemKeys"] });
  } else if (value.itemKeys && value.threadKeys && JSON.stringify(value.itemKeys) !== JSON.stringify(value.threadKeys)) {
    ctx.addIssue({ code: "custom", message: "itemKeys and threadKeys must match", path: ["itemKeys"] });
  }
  const itemKeys = value.itemKeys ?? value.threadKeys;
  if (value.anchorItemKey && itemKeys && !itemKeys.includes(value.anchorItemKey)) {
    ctx.addIssue({
      code: "custom",
      message: "anchorItemKey must be included in itemKeys",
      path: ["anchorItemKey"],
    });
  }
}).transform((value) => ({ ...value, itemKeys: value.itemKeys ?? value.threadKeys! }));

export const updateMessengerCustomGroupSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  icon: z.string().trim().min(1).max(24).optional().nullable(),
  collapsed: z.boolean().optional(),
  pinned: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const reorderMessengerCustomGroupsSchema = z.object({
  groupIds: z.array(z.string().uuid()).max(200),
});

export const assignMessengerCustomGroupEntrySchema = z.object({
  itemKey: messengerItemKeySchema.optional(),
  threadKey: messengerItemKeySchema.optional(),
}).superRefine((value, ctx) => {
  if (!value.itemKey && !value.threadKey) {
    ctx.addIssue({ code: "custom", message: "itemKey or threadKey is required", path: ["itemKey"] });
  } else if (value.itemKey && value.threadKey && value.itemKey !== value.threadKey) {
    ctx.addIssue({ code: "custom", message: "itemKey and threadKey must match", path: ["itemKey"] });
  }
}).transform((value) => ({ ...value, itemKey: value.itemKey ?? value.threadKey! }));

export const reorderMessengerCustomGroupEntriesSchema = z.object({
  itemKeys: z.array(messengerItemKeySchema).max(500).optional(),
  threadKeys: z.array(messengerItemKeySchema).max(500).optional(),
}).superRefine((value, ctx) => {
  if (!value.itemKeys && !value.threadKeys) {
    ctx.addIssue({ code: "custom", message: "itemKeys or threadKeys is required", path: ["itemKeys"] });
  } else if (value.itemKeys && value.threadKeys && JSON.stringify(value.itemKeys) !== JSON.stringify(value.threadKeys)) {
    ctx.addIssue({ code: "custom", message: "itemKeys and threadKeys must match", path: ["itemKeys"] });
  }
  const resolvedKeys = value.itemKeys ?? value.threadKeys;
  if (resolvedKeys && new Set(resolvedKeys).size !== resolvedKeys.length) {
    ctx.addIssue({
      code: "custom",
      message: "Messenger custom group reorder keys must be unique",
      path: [value.itemKeys ? "itemKeys" : "threadKeys"],
    });
  }
}).transform((value) => ({ ...value, itemKeys: value.itemKeys ?? value.threadKeys! }));

export type ChatConversationStatus = z.infer<typeof chatConversationStatusSchema>;
export type ChatIssueCreationMode = z.infer<typeof chatIssueCreationModeSchema>;
export type ChatMessageRole = z.infer<typeof chatMessageRoleSchema>;
export type ChatMessageKind = z.infer<typeof chatMessageKindSchema>;
export type ChatMessageStatus = z.infer<typeof chatMessageStatusSchema>;
export type ChatContextEntityType = z.infer<typeof chatContextEntityTypeSchema>;
export type CreateChatContextLink = z.infer<typeof createChatContextLinkSchema>;
export type CreateChatConversation = z.infer<typeof createChatConversationSchema>;
export type ChatDraft = z.infer<typeof chatDraftSchema>;
export type CreateChatFirstTurn = z.infer<typeof createChatFirstTurnSchema>;
export type ForkChatConversation = z.infer<typeof forkChatConversationSchema>;
export type CreateSideChat = z.infer<typeof createSideChatSchema>;
export type SetChatProjectContext = z.infer<typeof setChatProjectContextSchema>;
export type UpdateChatConversation = z.infer<typeof updateChatConversationSchema>;
export type AddChatMessage = z.input<typeof addChatMessageSchema>;
export type UpdateMessengerThreadUserState = z.infer<typeof updateMessengerThreadUserStateSchema>;
export type CreateMessengerCustomGroup = z.infer<typeof createMessengerCustomGroupSchema>;
export type CreateMessengerCustomGroupWithEntries = z.infer<typeof createMessengerCustomGroupWithEntriesSchema>;
export type UpdateMessengerCustomGroup = z.infer<typeof updateMessengerCustomGroupSchema>;
export type ReorderMessengerCustomGroups = z.infer<typeof reorderMessengerCustomGroupsSchema>;
export type AssignMessengerCustomGroupEntry = z.infer<typeof assignMessengerCustomGroupEntrySchema>;
export type ReorderMessengerCustomGroupEntries = z.infer<typeof reorderMessengerCustomGroupEntriesSchema>;
export type ChatQueuedMessagePayloadInput = z.input<typeof chatQueuedMessagePayloadSchema>;
export type CreateChatQueuedMessage = z.input<typeof createChatQueuedMessageSchema>;
export type UpdateChatQueuedMessage = z.input<typeof updateChatQueuedMessageSchema>;
export type CancelChatQueuedMessage = z.infer<typeof cancelChatQueuedMessageSchema>;
export type SteerChatQueuedMessage = z.infer<typeof steerChatQueuedMessageSchema>;
export type StopChatGeneration = z.infer<typeof stopChatGenerationSchema>;
export type ChatClientCheckpoint = z.infer<typeof chatClientCheckpointSchema>;
export type AppendChatGenerationEvent = z.infer<typeof appendChatGenerationEventSchema>;
export type ChatAskUserOption = z.infer<typeof chatAskUserOptionSchema>;
export type ChatAskUserQuestion = z.infer<typeof chatAskUserQuestionSchema>;
export type ChatAskUserRequest = z.infer<typeof chatAskUserRequestSchema>;
export type ChatRichReference = z.infer<typeof chatRichReferenceSchema>;
export type ChatAutomationCreate = z.infer<typeof chatAutomationCreateSchema>;
export type CreateChatAttachmentMetadata = z.infer<typeof createChatAttachmentMetadataSchema>;
export type ConvertChatToIssue = z.infer<typeof convertChatToIssueSchema>;
export type ChatOperationProposal = z.infer<typeof chatOperationProposalSchema>;
export type ResolveChatOperationProposal = z.infer<typeof resolveChatOperationProposalSchema>;
export type UpdateChatConversationUserState = z.infer<typeof updateChatConversationUserStateSchema>;
